import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { SEOHead } from '@/components/SEOHead';
import { Mail } from 'lucide-react';

export const TermsOfServicePage = () => {
  const { language } = useLanguage();

  const content = language === 'fr' ? {
    title: "Conditions d'utilisation",
    lastUpdated: "Dernière mise à jour : 24 juin 2025",
    sections: [
      {
        title: "1. Objet",
        content: "Les présentes conditions générales d'utilisation (CGU) ont pour objet de définir les modalités et conditions d'utilisation du site web Gaius et des services proposés par Gaius Legal AI."
      },
      {
        title: "2. Mentions légales",
        content: `Site édité par : Gaius Formation
SIRET : 80387645700026
Siège social : 75001, Paris, France
Email : contact@gaius.legal
Directeur de publication : Paul-Henri Levivier
Hébergement : www.lovable.com`
      },
      {
        title: "3. Accès au site",
        content: `L'accès au site est gratuit. Cependant, certains services peuvent être payants.
        Gaius se réserve le droit de suspendre, d'interrompre ou de limiter l'accès à tout ou partie du site, notamment pour des raisons de maintenance.`
      },
      {
        title: "4. Services proposés",
        content: `Gaius propose :
        • Des formations sur l'intelligence artificielle pour les professionnels du droit
        • Des contenus informatifs et pédagogiques
        • Des services de conseil et d'accompagnement
        
        Les informations fournies sont données à titre indicatif et ne constituent pas un conseil juridique personnalisé.`
      },
      {
        title: "5. Inscription et compte utilisateur",
        content: `L'inscription aux formations peut nécessiter la création d'un compte utilisateur.
        L'utilisateur s'engage à fournir des informations exactes et à les maintenir à jour.
        L'utilisateur est responsable de la confidentialité de ses identifiants de connexion.`
      },
      {
        title: "6. Obligations de l'utilisateur",
        content: `L'utilisateur s'engage à :
        • Utiliser le site conformément à sa destination
        • Ne pas porter atteinte aux droits de tiers
        • Ne pas diffuser de contenu illicite, diffamatoire ou contraire aux bonnes mœurs
        • Respecter les droits de propriété intellectuelle
        • Ne pas tenter de contourner les mesures de sécurité`
      },
      {
        title: "7. Propriété intellectuelle",
        content: `Tous les éléments du site (textes, images, logos, etc.) sont protégés par le droit d'auteur.
        Toute reproduction, même partielle, est interdite sans autorisation préalable.
        Les marques citées sont la propriété de leurs titulaires respectifs.`
      },
      {
        title: "8. Services payants",
        content: `Les formations peuvent être payantes. Les prix sont indiqués en euros TTC. Lorsque les prix sont indiqués hors taxes, cela est précisé.
Le paiement s'effectue en ligne par les moyens proposés.
En cas d'annulation, les conditions de remboursement seront précisées dans les conditions particulières.`
      },
      {
        title: "9. Responsabilité",
        content: `Gaius s'efforce d'assurer l'exactitude des informations diffusées mais ne peut garantir l'absence d'erreurs.
        Gaius ne saurait être tenu responsable des dommages directs ou indirects résultant de l'utilisation du site.
        L'utilisateur utilise le site sous sa seule responsabilité.`
      },
      {
        title: "10. Protection des données",
        content: "Le traitement des données personnelles est régi par notre Politique de confidentialité, consultable sur le site."
      },
      {
        title: "11. Droit applicable et juridiction",
        content: "Les présentes CGU sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents."
      },
      {
        title: "12. Modification des CGU",
        content: "Gaius se réserve le droit de modifier les présentes CGU à tout moment. Les modifications prennent effet dès leur publication sur le site."
      }
    ]
  } : {
    title: "Terms of Service",
    lastUpdated: "Last updated: June 24, 2025",
    sections: [
      {
        title: "1. Purpose",
        content: "These general terms of use (ToS) define the terms and conditions for using the Gaius website and services offered by Gaius Legal AI."
      },
      {
        title: "2. Legal Information",
        content: `Site published by: Gaius Formation
SIRET: 80387645700026
Headquarters: 75001, Paris, France
Email: contact@gaius.legal
Publication Director: Paul-Henri Levivier
Hosting: www.lovable.com`
      },
      {
        title: "3. Site Access",
        content: `Access to the site is free. However, some services may be paid.
        Gaius reserves the right to suspend, interrupt or limit access to all or part of the site, particularly for maintenance reasons.`
      },
      {
        title: "4. Services Offered",
        content: `Gaius offers:
        • Training on artificial intelligence for legal professionals
        • Informative and educational content
        • Consulting and support services
        
        The information provided is for informational purposes only and does not constitute personalized legal advice.`
      },
      {
        title: "5. Registration and User Account",
        content: `Registration for training may require creating a user account.
        The user undertakes to provide accurate information and keep it updated.
        The user is responsible for the confidentiality of their login credentials.`
      },
      {
        title: "6. User Obligations",
        content: `The user undertakes to:
        • Use the site in accordance with its intended purpose
        • Not infringe on third party rights
        • Not disseminate illicit, defamatory or inappropriate content
        • Respect intellectual property rights
        • Not attempt to circumvent security measures`
      },
      {
        title: "7. Intellectual Property",
        content: `All site elements (texts, images, logos, etc.) are protected by copyright.
        Any reproduction, even partial, is prohibited without prior authorization.
        Mentioned trademarks are the property of their respective owners.`
      },
      {
        title: "8. Paid Services",
        content: `Training may be paid. Prices are indicated in euros including tax. When pricing does not include tax, it is specified.
Payment is made online through the proposed methods.
In case of cancellation, refund conditions will be specified in the particular conditions.`
      },
      {
        title: "9. Liability",
        content: `Gaius strives to ensure the accuracy of disseminated information but cannot guarantee the absence of errors.
        Gaius cannot be held responsible for direct or indirect damages resulting from site use.
        The user uses the site under their sole responsibility.`
      },
      {
        title: "10. Data Protection",
        content: "Personal data processing is governed by our Privacy Policy, available on the site."
      },
      {
        title: "11. Applicable Law and Jurisdiction",
        content: "These ToS are subject to French law. In case of dispute, French courts will have exclusive jurisdiction."
      },
      {
        title: "12. ToS Modification",
        content: "Gaius reserves the right to modify these ToS at any time. Modifications take effect upon publication on the site."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={language === 'fr' ? "Conditions d'utilisation" : "Terms of Service"}
        description={language === 'fr' 
          ? "Conditions générales d'utilisation de Gaius - Formation IA pour avocats et juristes."
          : "Gaius Terms of Service - AI training for lawyers and legal professionals."}
        canonicalUrl="https://gaius-tech.com/terms-of-service"
        alternateUrlFr="https://gaius-tech.com/conditions-utilisation"
        alternateUrlEn="https://gaius-tech.com/terms-of-service"
      />
      <div className="bg-gradient-to-r from-primary to-purple-600 text-primary-foreground py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {content.title}
            </h1>
            <p className="text-blue-100">{content.lastUpdated}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="space-y-8">
            {content.sections.map((section, index) => (
              <div key={index}>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {section.title}
                </h2>
                <div className="text-gray-700 whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center space-x-2 text-gray-600">
              <Mail className="w-5 h-5" />
              <span>Contact : contact@gaius.legal</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
